import React from 'react';
import './BlogArt.css';

/**
 * Pure-CSS artwork for blog posts — no stock images. Each variant is a small
 * "artboard" in the brand language (window frame on a dotted canvas). The
 * whole graphic scales with its container via container queries, so the same
 * component works on cards and as the article hero.
 *
 * Variants: 'automation' | 'mobile' | 'design' | 'growth' | 'web' | 'accessibility'
 */

const VARIANTS = {
  /* Chat + Lead-Scoring: KI & Automatisierung */
  automation: (
    <div className="blog-art-body art-automation">
      <div className="aa-chat">
        <span className="aa-bubble aa-bubble-in" />
        <span className="aa-bubble aa-bubble-out" />
        <span className="aa-bubble aa-bubble-in aa-bubble-short" />
      </div>
      <div className="aa-panel">
        <span className="aa-panel-label" />
        <div className="aa-score">
          <span className="aa-score-bar" />
          <span className="aa-score-bar" />
          <span className="aa-score-bar aa-score-bar-high" />
        </div>
        <span className="aa-badge">✓</span>
      </div>
    </div>
  ),

  /* Phone-Skeleton: App-Entwicklung */
  mobile: (
    <div className="blog-art-body art-mobile">
      <div className="am-phone">
        <span className="am-notch" />
        <span className="am-line am-line-title" />
        <span className="am-card" />
        <span className="am-line" />
        <span className="am-line am-line-short" />
        <span className="am-fab">+</span>
      </div>
      <div className="am-chips">
        <span className="am-chip" />
        <span className="am-chip am-chip-accent" />
        <span className="am-chip" />
      </div>
    </div>
  ),

  /* Swatches + Typo: UI/UX Design */
  design: (
    <div className="blog-art-body art-design">
      <div className="ad-left">
        <span className="ad-type">Aa</span>
        <div className="ad-bars">
          <span style={{ width: '85%' }} />
          <span style={{ width: '60%' }} />
          <span style={{ width: '40%' }} />
        </div>
      </div>
      <div className="ad-right">
        <div className="ad-swatches">
          <span style={{ backgroundColor: '#166534' }} />
          <span style={{ backgroundColor: '#2ba84a' }} />
          <span style={{ backgroundColor: '#a7dcb8' }} />
          <span style={{ backgroundColor: '#1e293b' }} />
        </div>
        <span className="ad-button">Button</span>
      </div>
    </div>
  ),

  /* Chart + Trend: Online-Marketing */
  growth: (
    <div className="blog-art-body art-growth">
      <div className="ag-bars">
        <span style={{ height: '34%' }} />
        <span style={{ height: '52%' }} />
        <span style={{ height: '44%' }} />
        <span style={{ height: '70%' }} className="ag-bar-accent" />
        <span style={{ height: '88%' }} className="ag-bar-accent" />
      </div>
      <span className="ag-trend" aria-hidden="true">
        <svg viewBox="0 0 100 60" preserveAspectRatio="none" focusable="false">
          <path d="M2 52 L28 40 L52 44 L74 22 L98 8" fill="none" />
        </svg>
      </span>
      <span className="ag-chip">↗</span>
    </div>
  ),

  /* Site skeleton: Web-Entwicklung */
  web: (
    <div className="blog-art-body art-web">
      <div className="aw-page">
        <div className="aw-nav">
          <span className="aw-logo" />
          <span className="aw-nav-item" />
          <span className="aw-nav-item" />
          <span className="aw-nav-item aw-nav-cta" />
        </div>
        <div className="aw-hero">
          <span className="aw-hero-line" />
          <span className="aw-hero-line aw-hero-line-short" />
        </div>
        <div className="aw-grid">
          <span className="aw-card" />
          <span className="aw-card" />
          <span className="aw-card" />
        </div>
      </div>
    </div>
  ),

  /* Contrast toggle + screen reader waves: Barrierefreiheit */
  accessibility: (
    <div className="blog-art-body art-accessibility">
      <div className="aac-contrast" aria-hidden="true">
        <span className="aac-contrast-half aac-contrast-dark" />
        <span className="aac-contrast-half aac-contrast-light" />
      </div>
      <div className="aac-panel">
        <div className="aac-type-steps">
          <span className="aac-type-step">A</span>
          <span className="aac-type-step aac-type-step-mid">A</span>
          <span className="aac-type-step aac-type-step-lg">A</span>
        </div>
        <div className="aac-reader">
          <span className="aac-reader-icon">◐</span>
          <span className="aac-wave" />
          <span className="aac-wave aac-wave-mid" />
          <span className="aac-wave aac-wave-lg" />
        </div>
        <span className="aac-badge">✓</span>
      </div>
    </div>
  ),
};

const BlogArt = ({ variant = 'design', size = 'card' }) => (
  <div className={`blog-art blog-art-${size}`} aria-hidden="true">
    <div className="blog-art-canvas">
      <div className="blog-art-window">
        <div className="blog-art-titlebar">
          <i />
          <i />
          <i />
        </div>
        {VARIANTS[variant] || VARIANTS.design}
      </div>
    </div>
  </div>
);

export default BlogArt;
